import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsService } from '../../services/settingsService';
import toast from 'react-hot-toast';
import { EnvelopeIcon, DevicePhoneMobileIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const EmailSmsSettings = () => {
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [isTesting, setIsTesting] = useState(false);

  const { data, isLoading } = useQuery(
    'notification-settings',
    () => settingsService.getSystemConfig('notification')
  );

  const updateMutation = useMutation(
    ({ key, value }) => settingsService.updateSystemConfig(key, value),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('notification-settings');
        toast.success('Settings updated');
      }
    }
  );

  const testEmailMutation = useMutation(
    (email) => settingsService.testEmail(email),
    {
      onSuccess: () => {
        toast.success('Test email sent successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to send test email');
      },
      onSettled: () => {
        setIsTesting(false);
      }
    }
  );

  const testSmsMutation = useMutation(
    (phone) => settingsService.testSms(phone),
    {
      onSuccess: () => {
        toast.success('Test SMS sent successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to send test SMS');
      },
      onSettled: () => {
        setIsTesting(false);
      }
    }
  );

  const handleTestEmail = () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    setIsTesting(true);
    testEmailMutation.mutate(testEmail);
  };

  const handleTestSms = () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setIsTesting(true);
    testSmsMutation.mutate(testPhone);
  };

  const configData = data?.data || {};
  const notificationConfigs = configData['notification'] || [];

  const getConfigValue = (key, defaultValue = '') => {
    const config = notificationConfigs.find(c => c.config_key === key);
    return config?.config_value || defaultValue;
  };

  const updateConfig = (key, value) => {
    updateMutation.mutate({ key, value });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Email Settings */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b">
          <h3 className="font-semibold flex items-center">
            <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-600" />
            Email Configuration
          </h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">SMTP Server</label>
              <input
                type="text"
                value={getConfigValue('smtp_host', 'smtp.gmail.com')}
                onChange={(e) => updateConfig('smtp_host', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">SMTP Port</label>
              <input
                type="text"
                value={getConfigValue('smtp_port', '587')}
                onChange={(e) => updateConfig('smtp_port', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email Username</label>
              <input
                type="email"
                value={getConfigValue('smtp_user', '')}
                onChange={(e) => updateConfig('smtp_user', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email Password</label>
              <input
                type="password"
                value={getConfigValue('smtp_password', '')}
                onChange={(e) => updateConfig('smtp_password', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">From Email</label>
              <input
                type="email"
                value={getConfigValue('from_email', '')}
                onChange={(e) => updateConfig('from_email', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">From Name</label>
              <input
                type="text"
                value={getConfigValue('from_name', 'Samity Management')}
                onChange={(e) => updateConfig('from_name', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <label className="block text-sm font-medium mb-2">Test Email</label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 border rounded-lg"
              />
              <button
                onClick={handleTestEmail}
                disabled={isTesting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Send Test
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SMS Settings */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b">
          <h3 className="font-semibold flex items-center">
            <DevicePhoneMobileIcon className="h-5 w-5 mr-2 text-gray-600" />
            SMS Configuration
          </h3>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">SMS Gateway URL</label>
              <input
                type="text"
                value={getConfigValue('sms_gateway_url', '')}
                onChange={(e) => updateConfig('sms_gateway_url', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">API Key</label>
              <input
                type="text"
                value={getConfigValue('sms_api_key', '')}
                onChange={(e) => updateConfig('sms_api_key', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sender ID</label>
              <input
                type="text"
                value={getConfigValue('sms_sender_id', 'SamitySMS')}
                onChange={(e) => updateConfig('sms_sender_id', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Enable SMS</label>
              <select
                value={getConfigValue('sms_enabled', 'false')}
                onChange={(e) => updateConfig('sms_enabled', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <label className="block text-sm font-medium mb-2">Test SMS</label>
            <div className="flex space-x-2">
              <input
                type="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter phone number"
                className="flex-1 px-3 py-2 border rounded-lg"
              />
              <button
                onClick={handleTestSms}
                disabled={isTesting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Send Test
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Events */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b">
          <h3 className="font-semibold">Notification Events</h3>
        </div>
        <div className="divide-y">
          {[
            { key: 'notify_member_registration', label: 'Member Registration' },
            { key: 'notify_member_approval', label: 'Member Approval' },
            { key: 'notify_loan_application', label: 'Loan Application' },
            { key: 'notify_loan_approval', label: 'Loan Approval' },
            { key: 'notify_loan_disbursement', label: 'Loan Disbursement' },
            { key: 'notify_installment_reminder', label: 'Installment Reminder' },
            { key: 'notify_installment_overdue', label: 'Installment Overdue' },
            { key: 'notify_collection_submitted', label: 'Collection Submitted' }
          ].map((event) => (
            <div key={event.key} className="px-4 py-3 flex justify-between items-center hover:bg-gray-50">
              <span className="text-sm text-gray-700">{event.label}</span>
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={getConfigValue(`${event.key}_email`, 'true') === 'true'}
                    onChange={(e) => updateConfig(`${event.key}_email`, e.target.checked.toString())}
                    className="rounded"
                  />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={getConfigValue(`${event.key}_sms`, 'false') === 'true'}
                    onChange={(e) => updateConfig(`${event.key}_sms`, e.target.checked.toString())}
                    className="rounded"
                  />
                  <span className="text-sm">SMS</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailSmsSettings;